import React, { useRef, useState, useEffect } from 'react';
import { confirmAlert, Button } from 'components/common';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPlus, FaList, FaEllipsisV } from 'react-icons/fa';
import { MdEdit, MdDoneAll, MdDeleteForever } from 'react-icons/md';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { deleteRequestTerminalAccount } from 'redux/request/terminalAccountSlice';
import CloseRequestTerminalAccountConfirm from './CloseRequestTerminalAccountConfirm';
import EditRequestTerminalAccountForm from './EditRequestTerminalAccountForm';

const RequesTerminalAccountDetailHeader = ({ record }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showRequestForm, setShowRequestForm] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const dropdownRef = useRef(null);

  const { allow_edit, allow_delete, allow_close } = record?.permissions || {};

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNewRequest = () => {
    navigate('/request/terminal-accounts/new');
  };

  const handleRequestList = () => {
    navigate('/request/terminal-accounts/list');
  };

  const handleEditRequest = () => {
    setDropdownOpen(false);
    setShowRequestForm(true);
  };

  const handleDeleteRequest = () => {
    setDropdownOpen(false);
    confirmAlert({
      title: 'Confirmation',
      message: 'Are you sure you want to delete this request?',
      closeOnEscape: true,
      closeOnClickOutside: false,
      buttons: [
        {
          label: 'Yes',
          className: 'confirm-btn-danger',
          onClick: async () => {
            try {
              await dispatch(deleteRequestTerminalAccount(record.id)).unwrap();
              toast.success(`Request ${record.id} deleted successfully.`);
              handleRequestList();
            } catch (error) {
              toast.error('Failed to delete the request. Please try again.');
            }
          }
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };

  const hasDropdownItems = allow_edit || allow_delete;

  return (
    <>
      <div className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-slate-100 tw-mb-3">
        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
          >
            <FaArrowLeft />
          </Button>

          <div className="tw-flex tw-items-center tw-gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNewRequest}
            >
              <FaPlus />
              <span className="tw-hidden xl:tw-inline-block">New Request</span>
            </Button>

            {allow_close && (
              <Button
                variant="success"
                size="sm"
                onClick={() => setShowCloseConfirm(true)}
              >
                <MdDoneAll />
                <span className="tw-hidden xl:tw-inline-block">Close request</span>
              </Button>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={handleRequestList}
            >
              <FaList />
              <span className="tw-hidden xl:tw-inline-block">Back to list</span>
            </Button>

            {hasDropdownItems && (
              <div className="tw-relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setDropdownOpen((prev) => !prev)}
                  className="tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-md tw-border tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-transition-colors tw-cursor-pointer"
                  aria-label="More actions"
                >
                  <FaEllipsisV size={12} />
                </button>

                {dropdownOpen && (
                  <div className="tw-absolute tw-right-0 tw-mt-1 tw-w-40 tw-bg-white tw-border tw-border-slate-200 tw-rounded tw-shadow-lg tw-z-50 tw-py-1">
                    {allow_edit && (
                      <>
                        <button
                          type="button"
                          onClick={handleEditRequest}
                          className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-text-slate-700 hover:tw-bg-slate-50 tw-transition-colors tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-left"
                        >
                          <MdEdit className="tw-text-blue-500" />
                          Edit
                        </button>
                        {allow_delete && (
                          <div className="tw-border-t tw-border-slate-100 tw-my-0.5" />
                        )}
                      </>
                    )}

                    {allow_delete && (
                      <button
                        type="button"
                        onClick={handleDeleteRequest}
                        className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-text-slate-700 hover:tw-bg-slate-50 tw-transition-colors tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-left"
                      >
                        <MdDeleteForever className="tw-text-red-500" />
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showRequestForm && (
        <EditRequestTerminalAccountForm
          open={showRequestForm}
          setOpen={setShowRequestForm}
          record={record}
        />
      )}

      {showCloseConfirm && (
        <CloseRequestTerminalAccountConfirm
          open={showCloseConfirm}
          setOpen={setShowCloseConfirm}
          record={record}
        />
      )}
    </>
  );
};

RequesTerminalAccountDetailHeader.propTypes = {
  record: PropTypes.object.isRequired
};

export default RequesTerminalAccountDetailHeader;
